import React from 'react';
import { MarketplaceEconomicsDto } from '../../types';
import PlatformFeeSection from './PlatformFeeSection';
import ShippingSizeBandSection from './ShippingSizeBandSection';

export type MarketplaceEconomicsField = keyof Omit<MarketplaceEconomicsDto, 'id'>;

interface MarketplaceEconomicsSectionProps {
    economics: MarketplaceEconomicsDto;
    onChange: (field: MarketplaceEconomicsField, value: string | number | boolean) => void;
    onSave: () => void;
}

/**
 * Admin-only economics (adminAxios → /api/admin/marketplace-economics).
 * Separate from Website Settings branding.
 */
const MarketplaceEconomicsSection: React.FC<MarketplaceEconomicsSectionProps> = ({
    economics,
    onChange,
    onSave,
}) => {
    return (
        <div className="space-y-6">
            <div className="rounded-lg border border-amber-200 bg-amber-50/90 px-5 py-4">
                <h1 className="text-2xl font-bold text-amber-950">Marketplace economics</h1>
                <p className="mt-1 text-sm text-amber-900/85">
                    Tax, shipping fee, and platform fee (admin API only). Not on shop public endpoints. Branding is
                    under <strong>Website Settings</strong>.
                </p>
            </div>

            <section className="rounded-lg bg-white p-8 shadow-lg">
                <h2 className="mb-2 text-xl font-bold text-gray-800">Country, currency &amp; tax reporting</h2>
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">Country (ISO)</label>
                        <input
                            type="text"
                            maxLength={2}
                            value={economics.defaultCountryCode || 'BE'}
                            onChange={(e) => onChange('defaultCountryCode', e.target.value.toUpperCase())}
                            className="w-full rounded-lg border border-gray-300 px-4 py-2"
                        />
                    </div>
                    <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">Currency (ISO)</label>
                        <input
                            type="text"
                            maxLength={3}
                            value={economics.currencyCode || 'EUR'}
                            onChange={(e) => onChange('currencyCode', e.target.value.toUpperCase())}
                            className="w-full rounded-lg border border-gray-300 px-4 py-2"
                        />
                    </div>
                    <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">Tax scheme</label>
                        <input
                            type="text"
                            value={economics.taxReportingScheme || 'DAC7'}
                            onChange={(e) => onChange('taxReportingScheme', e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-4 py-2"
                        />
                    </div>
                    <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">Item threshold</label>
                        <input
                            type="number"
                            min={1}
                            value={economics.taxItemThreshold ?? 30}
                            onChange={(e) => onChange('taxItemThreshold', Number(e.target.value) || 30)}
                            className="w-full rounded-lg border border-gray-300 px-4 py-2"
                        />
                    </div>
                    <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">Amount threshold</label>
                        <input
                            type="number"
                            min={0}
                            step="0.01"
                            value={economics.taxAmountThreshold ?? 2000}
                            onChange={(e) => onChange('taxAmountThreshold', Number(e.target.value) || 2000)}
                            className="w-full rounded-lg border border-gray-300 px-4 py-2"
                        />
                    </div>
                    <div>
                        <label className="mb-2 block text-sm font-medium text-gray-700">Tax year time zone</label>
                        <input
                            type="text"
                            value={economics.taxYearTimeZoneId || 'Europe/Brussels'}
                            onChange={(e) => onChange('taxYearTimeZoneId', e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-4 py-2"
                        />
                    </div>
                    <div className="flex items-center gap-2 md:col-span-3">
                        <input
                            id="taxReportingEnabled"
                            type="checkbox"
                            checked={economics.taxReportingEnabled !== false}
                            onChange={(e) => onChange('taxReportingEnabled', e.target.checked)}
                            className="h-4 w-4"
                        />
                        <label htmlFor="taxReportingEnabled" className="text-sm text-gray-700">
                            Tax reporting enabled
                        </label>
                    </div>
                </div>
            </section>

            {/* One bracket: shipping toggle/title/fallback + size bands (same topic, two data stores) */}
            <section className="rounded-lg bg-white p-8 shadow-lg">
                <h2 className="mb-1 text-xl font-bold text-gray-800">Buyer shipping (Bpost)</h2>
                <p className="mb-4 text-sm text-gray-500">
                    One place for buyer postage: master on/off and line title, then size bands for what
                    sellers pick and what checkout charges. Band amounts are preferred; fallback amount is
                    only used when a listing has no valid size band.
                </p>

                <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-4">
                    <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-600">
                        Settings
                    </h3>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                        <div className="flex items-center gap-2 md:col-span-3">
                            <input
                                id="shippingFeeEnabled"
                                type="checkbox"
                                checked={economics.shippingFeeEnabled !== false}
                                onChange={(e) => onChange('shippingFeeEnabled', e.target.checked)}
                                className="h-4 w-4"
                            />
                            <label htmlFor="shippingFeeEnabled" className="text-sm text-gray-700">
                                Charge buyer shipping fee at checkout
                            </label>
                        </div>
                        <div>
                            <label className="mb-2 block text-sm font-medium text-gray-700">
                                Fallback amount (EUR)
                            </label>
                            <input
                                type="number"
                                min={0}
                                step="0.01"
                                value={economics.domesticShippingAmount ?? 4.95}
                                onChange={(e) =>
                                    onChange(
                                        'domesticShippingAmount',
                                        Number(e.target.value) >= 0 ? Number(e.target.value) : 4.95
                                    )
                                }
                                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2"
                            />
                            <p className="mt-1 text-xs text-gray-500">
                                Rare path only — normal prices are set per band below.
                            </p>
                        </div>
                        <div className="md:col-span-2">
                            <label className="mb-2 block text-sm font-medium text-gray-700">
                                Checkout line title
                            </label>
                            <input
                                type="text"
                                value={economics.domesticShippingTitle || 'Shipping (Bpost Belgium)'}
                                onChange={(e) => onChange('domesticShippingTitle', e.target.value)}
                                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2"
                            />
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onSave}
                        className="mt-4 rounded-lg bg-indigo-600 px-6 py-2 text-sm font-medium text-white shadow hover:bg-indigo-700"
                    >
                        Save shipping settings
                    </button>
                </div>

                <ShippingSizeBandSection embedded />
            </section>

            <section className="rounded-lg bg-white p-8 shadow-lg">
                <h2 className="mb-2 text-xl font-bold text-gray-800">Buyer platform fee</h2>
                <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
                    <div className="flex items-center gap-2 md:col-span-3">
                        <input
                            id="platformFeeEnabled"
                            type="checkbox"
                            checked={economics.platformFeeEnabled !== false}
                            onChange={(e) => onChange('platformFeeEnabled', e.target.checked)}
                            className="h-4 w-4"
                        />
                        <label htmlFor="platformFeeEnabled" className="text-sm text-gray-700">
                            Charge buyer platform fee at checkout
                        </label>
                    </div>
                    <div className="md:col-span-2">
                        <label className="mb-2 block text-sm font-medium text-gray-700">Checkout line title</label>
                        <input
                            type="text"
                            value={economics.platformFeeTitle || 'Buyer protection'}
                            onChange={(e) => onChange('platformFeeTitle', e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-4 py-2"
                        />
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onSave}
                    className="mt-6 rounded-lg bg-indigo-600 px-8 py-3 font-medium text-white shadow hover:bg-indigo-700"
                >
                    Save economics settings
                </button>
            </section>

            <PlatformFeeSection />
        </div>
    );
};

export default MarketplaceEconomicsSection;
